import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  HStack,
  Input,
  List,
  ListItem,
  Text,
  useColorMode,
} from "@chakra-ui/react";

const TICKER_RE = /^[A-Za-z0-9.\-]{1,10}$/;
// Same free symbol-search endpoint used by LogTrade. No API key, supports
// partial matches; we map the response to {symbol, name} pairs and let the
// user pick a suggestion or submit whatever they typed.
const SYMBOL_SEARCH_URL = "https://ticker-2e1ica8b9.now.sh/keyword";

export default function TickerSearch({
  onSubmit,
  placeholder = "Search ticker (e.g. AAPL)",
  submitLabel = "Go",
  submitColorScheme = "blue",
  size = "sm",
  clearOnSubmit = true,
  initialValue = "",
  isSubmitting = false,
}) {
  const { colorMode } = useColorMode();
  const [draft, setDraft] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const query = draft.trim();
    if (!query) {
      setSuggestions([]);
      return undefined;
    }
    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${SYMBOL_SEARCH_URL}/${encodeURIComponent(query)}`,
          controller ? { signal: controller.signal } : undefined
        );
        const rows = Array.isArray(res.data) ? res.data : [];
        setSuggestions(
          rows
            .filter((r) => r && r.symbol)
            .slice(0, 8)
            .map((r) => ({
              symbol: String(r.symbol).toUpperCase(),
              name: r.name || "",
            }))
        );
      } catch (err) {
        if (!axios.isCancel?.(err) && err?.name !== "CanceledError") {
          // Network blocked / offline — silently fall back to raw input.
          setSuggestions([]);
        }
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller?.abort();
    };
  }, [draft]);

  useEffect(() => {
    const handleClickAway = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickAway);
    return () => document.removeEventListener("mousedown", handleClickAway);
  }, []);

  const submit = (raw) => {
    const ticker = String(raw || "").trim().toUpperCase();
    if (!TICKER_RE.test(ticker)) return;
    onSubmit(ticker);
    if (clearOnSubmit) setDraft("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    submit(draft);
  };

  const isValid = TICKER_RE.test(draft.trim().toUpperCase());

  return (
    <Box ref={containerRef} position="relative" w="100%">
      <form onSubmit={handleSubmit} autoComplete="off">
        <HStack spacing={2}>
          <Input
            size={size}
            placeholder={placeholder}
            value={draft}
            onChange={(e) => setDraft(e.target.value.toUpperCase())}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setShowSuggestions(false);
            }}
            maxLength={10}
            aria-label={placeholder}
            textTransform="uppercase"
            fontFamily="mono"
            flex="1"
          />
          <Button
            size={size}
            colorScheme={submitColorScheme}
            type="submit"
            isDisabled={!isValid || isSubmitting}
            isLoading={isSubmitting}
            flexShrink={0}
          >
            {submitLabel}
          </Button>
        </HStack>
      </form>
      {showSuggestions && suggestions.length > 0 && (
        <List
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={20}
          mt={1}
          bg={colorMode === "light" ? "white" : "gray.800"}
          borderWidth="1px"
          borderRadius="md"
          boxShadow="md"
          maxH="220px"
          overflowY="auto"
          fontSize="sm"
        >
          {suggestions.map((s) => (
            <ListItem
              key={s.symbol}
              px={3}
              py={1.5}
              cursor="pointer"
              _hover={{ bg: colorMode === "light" ? "blue.50" : "blue.700" }}
              onMouseDown={(e) => {
                // mousedown beats the input's blur so the click registers.
                e.preventDefault();
                submit(s.symbol);
              }}
            >
              <Text as="span" fontWeight="bold">
                {s.symbol}
              </Text>
              {s.name ? (
                <Text as="span" color="gray.500" ml={2}>
                  {s.name}
                </Text>
              ) : null}
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}
